import React, { useState, useEffect } from 'react';
import { groupBy, sortBy } from 'lodash';
import { Form, Spin, Select, Checkbox, Radio, Input, Divider, Collapse, Tooltip } from 'antd';
import { LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import {
  getProjectById,
  getMarketIndicatorCategories,
  getMarketIndicatorsByProject
} from '../../../api/project';
import { getLocations } from '../../../api/location';
import { getProponents } from '../../../api/proponent';

import { Option } from 'antd/es/mentions';
import SelectProponent from '../dropdown/proponent';

interface ProjectAttributesFormProps {
  form: any;
}

const MITIGATION_TYPE_OPTIONS = [
  { value: 'Avoidance', label: 'Avoidance' },
  { value: 'Removals', label: 'Removals' },
  { value: 'Removals + Avoidance', label: 'Removals + Avoidance' },
];

const PROJECT_STAGE_OPTIONS = [
  { value: 'Pre-validation', label: 'Pre-validation' },
  { value: 'Validated', label: 'Validated' },
  { value: 'Verified', label: 'Verified' },
  { value: 'On hold', label: 'On hold' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'Late to verify', label: 'Late to verify' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Inactive', label: 'Inactive' },
];

// Categories where only one indicator can be active at a time
const SINGLE_SELECT_CATEGORIES = new Set(['ccp', 'article_6']);

interface MarketIndicatorDef {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  color_hex?: string;
  sort_order: number;
  category_id: string;
}

interface Category {
  id: string;
  name: string;
  display_name: string;
  sort_order: number;
  market_indicators: MarketIndicatorDef[];
}

const ProjectAttributesForm: React.FC<ProjectAttributesFormProps> = ({ form }) => {
  const { id, creditingPeriodId } = useParams<{ id; creditingPeriodId: string }>();
  const [locationType, setLocationType] = useState<string>();
  const [proponents, setProponents] = useState([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeIndicators, setActiveIndicators] = useState<Set<string>>(new Set());
  // Tracks the selected indicator name for single-select categories (ccp, article_6)
  const [singleSelections, setSingleSelections] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [projectData, locationData, proponentsData] = await Promise.all([
          getProjectById(id, Number(creditingPeriodId)),
          getLocations(),
          getProponents(),
        ]);

        const creditingPeriod = projectData.response.project_crediting_periods.find(
          (cp) => cp.crediting_period_id === Number(creditingPeriodId)
        );

        setProponents(
          proponentsData.response.map((proponent) => ({
            id: proponent.id,
            display_name: proponent.display_name,
            name: proponent.name,
          }))
        );
        setLocations(locationData.response);

        // Fetch market indicator categories and per-CP states (non-blocking)
        try {
          const [categoriesData, indicatorStates] = await Promise.all([
            getMarketIndicatorCategories(),
            getMarketIndicatorsByProject(id),
          ]);

          const rawCategories = categoriesData.response || categoriesData;
          const sortedCategories = sortBy(
            Array.isArray(rawCategories) ? rawCategories : [],
            'sort_order'
          );
          setCategories(sortedCategories);

          const indicators = indicatorStates.response || indicatorStates;
          const indicatorFormValues: Record<string, any> = {};
          const initialActive = new Set<string>();
          const initialSingleSelections: Record<string, string | undefined> = {};

          // Build a name -> category map from the categories data
          const indicatorToCategoryName: Record<string, string> = {};
          sortedCategories.forEach((cat) => {
            (cat.market_indicators || []).forEach((mi) => {
              indicatorToCategoryName[mi.name] = cat.name;
            });
          });

          (Array.isArray(indicators) ? indicators : []).forEach((state: any) => {
            const miName = state.market_indicator.name;
            indicatorFormValues[miName] = {
              is_active: state.is_active,
              extra_content: state.extra_content,
            };
            if (state.is_active) {
              initialActive.add(miName);
              const catName = indicatorToCategoryName[miName];
              if (catName && SINGLE_SELECT_CATEGORIES.has(catName)) {
                initialSingleSelections[catName] = miName;
              }
            }
          });
          setActiveIndicators(initialActive);
          setSingleSelections(initialSingleSelections);
          form.setFieldsValue(indicatorFormValues);
        } catch (err) {
          console.error('Error fetching market labels:', err);
        }

        const stateExist = projectData.response.locations.find(
          (location) => location.location.type === 'state'
        );
        setLocationType(stateExist ? 'state' : 'country');

        setLoading(false);
        form.setFieldsValue({
          projectStage: projectData.response.project_stage || undefined,
          mitigationType: creditingPeriod.mitigation_type || undefined,
          ...(stateExist && {
            projectState: projectData.response.locations
              .filter((location) => location.location.type === 'state')
              .map((location) => location.location.id),
          }),
          ...(!stateExist && {
            projectCountry: projectData.response.locations.map(
              (location) => location.location.id
            )[0],
          }),
          proponent: projectData.response.proponents.map(
            (proponent) => proponent.proponent.id
          ),
          locationType: stateExist ? 'state' : 'country',
          onWatch: projectData.response.onwatch === 1 ? true : false,
          onWatchReason: projectData.response.onwatch_reason,
        });
      } catch (error) {
        setLoading(false);
        console.error('Error fetching project:', error);
      }
    }

    fetchData();
  }, [id, form]);

  /** For single-select categories: select one indicator, deactivate all others */
  const handleSingleSelect = (category: Category, selectedName: string | undefined) => {
    const indicatorNames = category.market_indicators.map((mi) => mi.name);
    setSingleSelections((prev) => ({ ...prev, [category.name]: selectedName }));
    setActiveIndicators((prev) => {
      const next = new Set(prev);
      indicatorNames.forEach((name) => next.delete(name));
      if (selectedName) next.add(selectedName);
      return next;
    });
    // Sync form fields
    indicatorNames.forEach((name) => {
      form.setFieldValue([name, 'is_active'], name === selectedName);
    });
  };

  /** For multi-select categories: toggle one indicator */
  const handleMultiToggle = (indicatorName: string, checked: boolean) => {
    setActiveIndicators((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(indicatorName);
      } else {
        next.delete(indicatorName);
      }
      return next;
    });
  };

  const handleLocationTypeChange = (e) => {
    setLocationType(e.target.value);
  };

  const getLocationCountries = () => {
    return locations
      .filter((location) => location.type === 'country')
      .map((country) => (
        <Option key={country.id} value={country.id}>
          {country.display_name}
        </Option>
      ));
  };

  const getLocationStates = (): any => {
    const grouped = groupBy(
      locations.filter((location) => location.type === 'state'),
      'parent.display_name'
    );
    return (Object.entries(grouped) as [string, any[]][]).map(([groupName, types]) => ({
      group: groupName || 'no-parent',
      types: types.map((type: any) => ({ id: type.id, display_name: type.display_name })),
    }));
  };

  const normalizeString = (str) => {
    return str
      ?.normalize('NFD')
      ?.replace(/[\u0300-\u036f]/g, '')
      ?.toLowerCase();
  };

  const renderSingleSelectCategory = (category: Category) => {
    const sorted = sortBy(category.market_indicators, 'sort_order');
    const selected = singleSelections[category.name];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Select
          value={selected}
          onChange={(value) => handleSingleSelect(category, value)}
          placeholder={`Select ${category.display_name} status`}
          allowClear
          onClear={() => handleSingleSelect(category, undefined)}
          style={{ width: '100%' }}
          disabled
        >
          {sorted.map((indicator) => (
            <Select.Option key={indicator.name} value={indicator.name}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {indicator.color_hex && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: indicator.color_hex,
                      display: 'inline-block',
                    }}
                  />
                )}
                {indicator.display_name}
              </span>
            </Select.Option>
          ))}
        </Select>
        {/* Hidden form fields to keep is_active in sync */}
        {sorted.map((indicator) => (
          <Form.Item
            key={indicator.name}
            name={[indicator.name, 'is_active']}
            valuePropName="checked"
            hidden
          >
            <Checkbox disabled />
          </Form.Item>
        ))}
      </div>
    );
  };

  const renderMultiSelectCategory = (category: Category) => {
    const sorted = sortBy(category.market_indicators, 'sort_order');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map((indicator) => (
          <div
            key={indicator.name}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: '1px solid #f0f0f0',
              background: activeIndicators.has(indicator.name) ? '#f6ffed' : '#fafafa',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {indicator.color_hex && (
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: indicator.color_hex,
                    flexShrink: 0,
                  }}
                />
              )}
              <Form.Item
                name={[indicator.name, 'is_active']}
                valuePropName="checked"
                style={{ marginBottom: 0, flex: 1 }}
              >
                <Checkbox
                  onChange={(e) => handleMultiToggle(indicator.name, e.target.checked)}
                  disabled
                >
                  {indicator.display_name}
                </Checkbox>
              </Form.Item>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getActiveCount = (category: Category) => {
    return category.market_indicators.filter((i) => activeIndicators.has(i.name)).length;
  };

  return (
    <>
      <Spin
        spinning={loading}
        indicator={<LoadingOutlined style={{ fontSize: 24, color: 'white' }} spin />}
        fullscreen
      />
      <Form.Item
        label="Proponent"
        name="proponent"
        rules={[{ required: false, message: 'Please select Proponent!' }]}
      >
        <SelectProponent initialItems={proponents} form={form} />
      </Form.Item>
      <div className="flex gap-4">
        <Form.Item label="Location" name="locationType">
          <Radio.Group value={locationType} onChange={handleLocationTypeChange}>
            <Radio.Button value="country">Country</Radio.Button>
            <Radio.Button value="state">State</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {locationType === 'country' && (
          <Form.Item label=" " name="projectCountry" className="flex-1">
            <Select
              placeholder="Select Country"
              showSearch
              filterOption={(inputValue, option) => {
                const normalizedInput = normalizeString(inputValue);
                const normalizedOption = normalizeString(option?.children);
                return normalizedOption?.includes(normalizedInput);
              }}
            >
              {getLocationCountries()}
            </Select>
          </Form.Item>
        )}
        {locationType === 'state' && (
          <Form.Item label=" " name="projectState" className="flex-1">
            <Select
              mode="multiple"
              placeholder="Select States"
              showSearch
              filterOption={(inputValue, option) => {
                const normalizedInput = normalizeString(inputValue);
                const normalizedOption = normalizeString(option?.children);
                return normalizedOption?.includes(normalizedInput);
              }}
            >
              {getLocationStates().map((group) => (
                <Select.OptGroup
                  key={group.group}
                  label={<span style={{ fontWeight: 'bold' }}>{group.group}</span>}
                >
                  {group.types.map((type) => (
                    <Select.Option key={type.id} value={type.id}>
                      {type.display_name}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>
        )}
      </div>
      <Divider />

      {/* Project Stage */}
      <Form.Item
        label={
          <span>
            Project Stage&nbsp;
            <Tooltip title="This field is automatically set based on mapping rules">
              <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'default' }} />
            </Tooltip>
          </span>
        }
        name="projectStage"
        rules={[{ required: false }]}
      >
        <Select placeholder="Select Project Stage" allowClear disabled>
          {PROJECT_STAGE_OPTIONS.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* Mitigation Type */}
      <Form.Item
        label={
          <span>
            Mitigation Type&nbsp;
            <Tooltip title="This field is automatically set based on mapping rules">
              <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'default' }} />
            </Tooltip>
          </span>
        }
        name="mitigationType"
        rules={[{ required: false }]}
      >
        <Select placeholder="Select Mitigation Type" allowClear disabled>
          {MITIGATION_TYPE_OPTIONS.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Divider />

      {/* Market Indicators */}
      <h4 style={{ marginBottom: 12 }}>Market labels</h4>
      {categories.length > 0 && (
        <Collapse
          defaultActiveKey={[]}
          style={{ marginBottom: 24 }}
          items={categories.map((category) => {
            const isSingleSelect = SINGLE_SELECT_CATEGORIES.has(category.name);
            const activeCount = getActiveCount(category);
            return {
              key: category.name,
              label: (
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  {category.display_name}
                  {activeCount > 0 && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#fff',
                        backgroundColor: '#52c41a',
                        borderRadius: 10,
                        padding: '1px 8px',
                      }}
                    >
                      {activeCount} active
                    </span>
                  )}
                  {isSingleSelect && (
                    <span style={{ color: '#999', fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                      (single select)
                    </span>
                  )}
                </span>
              ),
              children: isSingleSelect
                ? renderSingleSelectCategory(category)
                : renderMultiSelectCategory(category),
            };
          })}
        />
      )}

      <Divider />
      <Form.Item
        label="On Watch"
        name="onWatch"
        rules={[{ required: false, message: 'Please select an option' }]}
      >
        <Radio.Group>
          <Radio.Button value={true}>Yes</Radio.Button>
          <Radio.Button value={false}>No</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label="Reason (On Watch)"
        name="onWatchReason"
        rules={[{ required: false, message: 'Please input On Watch Reason!' }]}
      >
        <Input.TextArea />
      </Form.Item>
    </>
  );
};

export default ProjectAttributesForm;
